import React, { useEffect, useRef, useState } from 'react';
import { makeStyles, Box, Typography, Switch, Button } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useDispatch, useSelector } from 'react-redux';
import { invokeGetService, setAiAutoProceed, setAiRequestEntity, updateAiAssistant } from '../../../store/global/actions';
import { getAiAssistantAutoProceed, getAiAssistantContext, getAiAssistantLoading } from '../../../store/global/selectors';
import { buildWorkflowSteps } from '../../../utils/llmUtils';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { AttachIcon } from '../../atoms/icons/AttachIcon';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 260,
    minWidth: 200,
    flexShrink: 0,
    backgroundColor: '#f9fafb',
    padding: theme.spacing(1.5, 1.5),
    borderRight: '1px solid #edf2f7',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  header: {
    color: '#94a3b8',
    letterSpacing: '1px',
    fontWeight: 700,
    fontSize: '0.75rem',
    marginBottom: theme.spacing(1.5),
  },
  stepRow: {
    display: 'flex',
    position: 'relative',
    padding: '7px 8px',
    borderRadius: '10px',
    alignItems: 'center',
    marginBottom: '3px',
    '&:hover': {
      backgroundColor: 'rgba(52, 68, 92, 0.04)', // Very subtle slate tint
    },
    '&:not(:last-child):before': {
      content: '""',
      position: 'absolute',
      left: 27,
      top: '50%',
      height: '100%',
      bottom: 0,
      width: 2,
      backgroundColor: '#e2e8f0',
    },
  },
  activeStepRow: {
    backgroundColor: 'rgba(52, 68, 92, 0.08)', // Stronger background for active
    '& $stepTitle': {
      color: '#34445c', // Brand Slate
      fontWeight: 800,
    },
    // The vertical yellow bar indicator
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '20%',
      height: '60%',
      width: 4,
      backgroundColor: '#3B82F6',
      borderRadius: '0 4px 4px 0',
    }
  },
  disableStepRow: {
    cursor: 'not-allowed !important',
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
  iconBox: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    fontSize: '0.8rem',
    fontWeight: 700,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    color: '#94a3b8',
    transition: 'all 0.2s',
  },
  activeIconBox: {
    backgroundColor: '#34445c', // Brand Slate
    color: '#ffea00',           // Brand Yellow text/icon
    borderColor: '#34445c',
    boxShadow: '0 4px 12px rgba(52, 68, 92, 0.2)',
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    marginRight: theme.spacing(2),
  },
  completedIcon: { backgroundColor: '#48bb78', color: '#fff' },
  enabledPendingIcon: {
    border: '2px solid rgba(30, 41, 59,0.5)',
    color: 'rgba(30, 41, 59,0.5)',
    background: '#fff'
  },
  activeIcon: {
    backgroundColor: '#fff',
    color: '#3B82F6',
    border: '2px solid #3B82F6',
    // animation: '$spin 3s linear infinite'
  },
  pendingIcon: { backgroundColor: '#fff', color: '#e2e8f0', border: '1px solid #e2e8f0' },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  },
  autoRunSubLabel: {
    fontSize: '0.65rem',
    color: '#94a3b8',
    marginTop: 4,
    lineHeight: 1.4,
  },
  autoRunWrapper: {
    marginTop: '1rem',
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid #edf2f7',
    flexShrink: 0,
    position: 'relative',
  },
  autoRunTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 0px',
    borderRadius: 8,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  },
  autoRunTriggerIcon: {
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    color: '#64748b',
  },
  autoRunTriggerLabel: {
    flex: 1,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#1e293b',
  },
  autoRunTriggerChevron: {
    fontSize: '0.75rem',
    color: '#48566a',
    display: 'flex',
    alignItems: 'center',
    transition: 'transform 0.2s',
  },
  dropdownMenu: {
    position: 'absolute',
    bottom: '110%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    overflow: 'hidden',
    zIndex: 100,
    padding: '4px',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
  },
  dropdownItemActive: {
    backgroundColor: '#f1f5f9',
  },
  dropdownItemIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '0.85rem',
    marginTop: 1,
  },
  dropdownItemTitle: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#1e293b',
    lineHeight: 1.3,
  },
  dropdownItemDesc: {
    fontSize: '0.68rem',
    color: '#94a3b8',
    lineHeight: 1.4,
    marginTop: 1,
  },
  resourcesBtn: ({isProgress}) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    width: "100%",
    padding: "9px 14px",
    background: "#fff",
    border: "1px solid #dde0ee",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 500,
    color: "#1a1d2e",
    cursor: "pointer",
    marginTop: 'auto',
    transition: "border-color 0.2s, background 0.2s, color 0.2s",
    "&:hover": {
      background: isProgress ? "#fff" : "rgba(59,130,246,0.06)",
      borderColor: isProgress ? '#dde0ee' : "#3B82F6",
      color: isProgress ? "#1a1d2e" : "#3B82F6",
      "& $fileCount": {
        background: "#3B82F6",
      },
    },
  }),
  fileCount: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 20,
    background: "#3B82F6",
    color: "#fff",
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1,
    transition: "background 0.2s",
  },
}));

const workflowSteps = [
  {
    title: 'System Model',
    key: 'system_model',
    status: 'Loading..',
    type: 'pending',
    isEnabled: false
  },
  {
    title: 'Database Model',
    key: 'database_profile',
    status: 'Loading..',
    type: 'pending',
    isEnabled: false
  },
  {
    title: 'Views',
    key: 'view',
    status: 'Loading..',
    type: 'pending',
    isEnabled: false
  },
  {
    title: 'Transactions',
    key: 'transaction',
    status: 'Loading..',
    type: 'pending',
    isEnabled: false
  },
  {
    title: 'Simulations',
    key: 'simulation',
    status: 'Loading..',
    type: 'pending',
    isEnabled: false
  },
]

const Sidebar = ({ activeStep, setActiveStep, aiGeneratedItems, isSummaryExist, waitingForAnswer }) => {
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  
  
  const assistantContext = useSelector(getAiAssistantContext)
  const isGenerationProgress = useSelector(getAiAssistantLoading)
  const autoProceed = useSelector(getAiAssistantAutoProceed);
  const classes = useStyles({isProgress: aiGeneratedItems.length > 0 || waitingForAnswer || isGenerationProgress});
  
  const [fileCount, setFileCount] = useState(0)
  const [steps, setSteps] = useState(workflowSteps);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  const isAutoRunActive = () => !!autoProceed && autoProceed !== 'off';

  const autoRunOptions = [
    {
      value: 'off',
      label: 'Manual',
      description: 'You control each step manually',
      helperText: 'You make every decision. No automated actions are taken without your explicit approval. Best for fine-grained, hands-on workflows.',
      icon: <img src={`${process.env.PUBLIC_URL}/ide_icons/manual_mode.svg`} width={18} height={18} />,
      iconBg: '#ecfdf5',
    },
    {
      value: 'review',
      label: 'Review',
      description: 'Pauses at each list for your review',
      helperText: 'Actions are suggested automatically, but require your sign-off before execution. A balance between speed and oversight.',
      icon: <VisibilityIcon style={{ color: "48566a", fontSize: 18 }} />,
      iconBg: '#ecfdf5',
    },
    {
      value: 'full',
      label: 'Auto',
      description: 'Generates all steps without stopping',
      helperText: 'Workflow generation proceeds automatically without pausing for approval. Ideal for rapid end-to-end workflow creation.',
      icon: <img src={`${process.env.PUBLIC_URL}/ide_icons/zap.svg`} width={18} height={18} />,
      iconBg: '#ecfdf5',
    },
  ];

  const currentOption = autoRunOptions.find(o => o.value === (autoProceed || 'full'));

  const handleOnRowClick = (data) => {
    if (!data.isEnabled || isGenerationProgress || aiGeneratedItems.length) return;

    dispatch(updateAiAssistant({
      requestContext: { entityType: data.key, entity: null, isCreate: true }
    }));
    setActiveStep(data.key);
  };


  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {

      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!steps?.length || (isGenerationProgress && !isAutoRunActive())) return;
    const isEveryStepCompleted = steps.every(item => item.type === "completed");
    if (!isEveryStepCompleted) {
      checkStepStatus(autoProceed);
    }
  }, [isGenerationProgress, assistantContext])

  useEffect(() => {
    if(isSummaryExist){
      setFileCount(1)
    }
  }, [isSummaryExist])

  // useEffect(() => {
  //   if (!dropdownOpen) return;
  //   const handleClickOutside = (e) => {
  //     setDropdownOpen(false);
  //   };
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => document.removeEventListener('mousedown', handleClickOutside);
  // }, [dropdownOpen]);


  const checkStepStatus = (autoProceed) => {
    setIsLoading(true);
    dispatch(invokeGetService('getBlueprintConstructs', []))
      .then(response => {
        if (response?.status && response.data) {
          const { updatedSteps, activeStep } = buildWorkflowSteps(
            workflowSteps,
            {
              clusterlets: response.data.clusterlets,
              transactions: response.data.transactions,
              events: response.data.events,
              views: response.data.views,
              databaseProfiles: response.data.databaseprofiles,
              testSuites: response.data.testSuites
            },
            assistantContext
          );
          if (!isAutoRunActive()) {
            setActiveStep(activeStep);
          }
          setSteps(updatedSteps);
          if (isAutoRunActive() && updatedSteps.every(s => s.type === 'completed')) {
            dispatch(setAiAutoProceed("off"));
          }
        }
        setIsLoading(false);
      });
  };


  const onAutoProceedChange = (value) => {
    dispatch(setAiAutoProceed(value));
  }

  const isWorkflowCompleted = steps.every(step => step.type === 'completed');

  return (
    <Box className={classes.root}>
      <Typography className={classes.header} style={{
        display: 'block',
        visibility: 'visible'
      }}>WORKFLOW PROGRESS</Typography>
      <Box style={{ display: 'flex', flexDirection: 'column' }}>
        {steps.map((step, i) => (
          <Box
            key={i}
            className={`${classes.stepRow} ${classes[step.key === activeStep ? 'activeStepRow' : '']} ${(step.isEnabled === false || isGenerationProgress || aiGeneratedItems?.length) ? classes.disableStepRow : ''}`}
            style={{ cursor: step.isEnabled && !isGenerationProgress && aiGeneratedItems.length === 0 ? 'pointer' : 'not-allowed' }}
            onClick={() => handleOnRowClick(step)}
          >
            <Box className={`${classes.iconContainer} ${step.key === activeStep ? classes.activeIcon : step.isEnabled && step.type === 'pending' ? classes.enabledPendingIcon : classes[`${step.type}Icon`]}`}>
              {
                step.type === "completed" ?
                  <CheckIcon fontSize="small" />
                  : i + 1
              }
            </Box>
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: !step.isEnabled ? '#cbd5e1' : '#1e293b' }}>
                {step.title}
              </Typography>
              <Typography variant="caption" style={{ color: '#94a3b8' }}>
                {step.key === activeStep ? "In Progress" : step.status}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <button 
        className={(isGenerationProgress || aiGeneratedItems?.length || waitingForAnswer) ? `${classes.resourcesBtn} ${classes.disableStepRow}` : classes.resourcesBtn} disabled={isGenerationProgress || aiGeneratedItems?.length || waitingForAnswer} 
        style={{opacity: isGenerationProgress || aiGeneratedItems?.length || waitingForAnswer ? 0.8 : 1 }}
        onClick={() => setActiveStep('resources')}
      >
        <AttachIcon width="14" height="14" />
        <span>Resources</span>
        {fileCount > 0 && (
          <span className={classes.fileCount}>{fileCount}</span>
        )}
      </button>
      {!isWorkflowCompleted && (
        <Box className={classes.autoRunWrapper} ref={dropdownRef}>

          {/* Dropdown Menu — renders above the trigger */}
          {dropdownOpen && (
            <Box className={classes.dropdownMenu}>
              {autoRunOptions.map((option) => (
                <Box
                  key={option.value}
                  className={`${classes.dropdownItem} ${(autoProceed || 'off') === option.value ? classes.dropdownItemActive : ''
                    }`}
                  onClick={() => {
                    onAutoProceedChange(option.value);
                    setDropdownOpen(false);
                  }}
                >
                  <Box
                    className={classes.dropdownItemIcon}
                    style={{ backgroundColor: option.iconBg }}
                  >
                    {option.icon}
                  </Box>
                  <Box>
                    <Typography className={classes.dropdownItemTitle}>
                      {option.label}
                    </Typography>
                    <Typography className={classes.dropdownItemDesc}>
                      {option.helperText}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Trigger Button */}
          <Box
            className={classes.autoRunTrigger}
            onClick={() => setDropdownOpen(prev => !prev)}
            style={{ opacity: 1, cursor: 'pointer' }}
          >
            <Box className={classes.autoRunTriggerIcon}>
              {currentOption?.icon}
            </Box>
            <Typography className={classes.autoRunTriggerLabel}>
              {currentOption?.label}
            </Typography>
            <Box
              className={classes.autoRunTriggerChevron}
              style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              ▲
            </Box>
          </Box>
          <Typography className={classes.autoRunSubLabel}>
            {currentOption.description}
          </Typography>

        </Box>
      )}
    </Box>
  );
};

export default Sidebar;